import React, { useEffect } from 'react';

import { useFlowEngine } from '../../hooks';
import type { FlowEngine } from '../../services/flows';
import type { Step } from '../../services/flows/types';
import { logger } from '../../utils/logger';

import ChipsStep from './ChipsStep';
import ImageEditStep from './ImageEditStep';
import ImageInputStep from './ImageInputStep';
import LLMStep from './LLMStep';
import SystemStep from './SystemStep';
import TextInputStep from './TextInputStep';


// Create component logger for StepRenderer
const stepRendererLogger = logger.withComponent('StepRenderer');

interface StepRendererProps {
  step: Step | null;
  engine?: FlowEngine; // Optional, will use context if not provided
  onChipSelect: (chipId: string) => void;
  onMessage: (message: any) => void;
}

const StepRenderer: React.FC<StepRendererProps> = ({
  step,
  engine: providedEngine,
  onChipSelect,
  onMessage,
}) => {
  // Use provided engine or fall back to context
  const contextEngine = useFlowEngine();
  const engine = providedEngine || contextEngine;

  useEffect(() => {
    stepRendererLogger.info('[StepRenderer] rendering step:', {
      id: step?.id,
      type: step?.type,
      engineExists: !!engine,
      engineSource: providedEngine ? 'props' : 'context',
      timestamp: performance.now(),
    });
  }, [step?.id, step?.type, engine, providedEngine]);

  if (!step || !engine) {
    stepRendererLogger.info('[StepRenderer] Missing step or engine:', { step: !!step, engine: !!engine });
    return null;
  }

  // Use step.id as key to ensure component remounts on step change
  const stepKey = step.id;

  switch (step.type) {
    case 'system':
      return (
        <SystemStep
          key={stepKey}
          step={step}
          engine={engine}
          onMessage={onMessage}
        />
      );

    case 'chips':
      return (
        <ChipsStep
          key={stepKey}
          step={step}
          engine={engine}
          onChipSelect={onChipSelect}
          onMessage={onMessage}
        />
      );

    case 'textInput':
      return (
        <TextInputStep
          key={stepKey}
          step={step as any}
          engine={engine}
          onMessage={onMessage}
        />
      );

    case 'imageInput':
      return (
        <ImageInputStep
          key={stepKey}
          step={step as any}
          engine={engine}
          onMessage={onMessage}
        />
      );

    case 'llm':
      return (
        <LLMStep
          key={stepKey}
          step={step as any}
          engine={engine}
          onMessage={onMessage}
        />
      );

    case 'imageEdit':
      return (
        <ImageEditStep
          key={stepKey}
          step={step as any}
          engine={engine}
          onMessage={onMessage}
        />
      );

    default:
      return (
        <div className="error-message">
          {__('Unsupported step type')}: {step.type}
        </div>
      );
  }
};

export default StepRenderer;